/**
 * Primary left navigation. Renders the {@link NAV_ITEMS} model grouped by
 * section, hiding any entry the live session can't access (RBAC), and marks the
 * active route via TanStack Router's `useRouterState`.
 *
 * ## Why the skip link is here
 *
 * The shell paints sidebar → header → main, and the sidebar alone is up to 26
 * links. Without a skip target, reaching page content by keyboard meant tabbing
 * past all of them on every route change. The link has to be the FIRST focusable
 * thing in the document, and this `<aside>` is the first element of the shell —
 * so it lives here rather than in the header, which renders after it.
 *
 * ## Why active state is more than a class
 *
 * `bg-accent` is invisible to a screen reader, so the active item was announced
 * identically to the other 25. `aria-current="page"` is what says which one you
 * are on. It is deliberately NOT set on the Ask-AI entry: that button opens a
 * drawer, it is not a page you can be on.
 */
import { Link, useRouterState } from "@tanstack/react-router";
import { usePermissions } from "@authe/ui-core/permissions";
import { cn } from "@authe/ui-core";
import { useAskAi } from "@/app/ask-ai-shell";
import { MAIN_CONTENT_ID } from "@/app/layoutIds";
import { APP_NAME, APP_TAGLINE, NAV_ITEMS, NAV_SECTIONS, ROUTES } from "@/config";

/** A section heading as a DOM id — `aria-labelledby` cannot reference one with spaces. */
function sectionId(section: string): string {
  return `nav-section-${section.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
}

export function Sidebar(): React.ReactElement {
  const { can } = usePermissions();
  const { openAskAi } = useAskAi();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const visible = NAV_ITEMS.filter(
    (item) => !item.resource || can(item.resource, item.action ?? "list"),
  );

  return (
    <aside aria-label="Sidebar" className="flex h-full w-60 flex-col border-r border-border bg-card">
      {/*
        Hidden until focused. The `href` is the real mechanism (it moves the
        document's sequential-navigation point, which a scripted `.focus()`
        alone does not), and the handler adds what a fragment jump cannot: an
        explicit focus on a container that isn't natively focusable.
      */}
      <a
        href={`#${MAIN_CONTENT_ID}`}
        className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded-md focus:bg-primary focus:px-3 focus:py-2 focus:text-sm focus:font-medium focus:text-primary-foreground focus:shadow-lg"
        onClick={() => {
          const main = document.getElementById(MAIN_CONTENT_ID);
          if (!main) return;
          if (!main.hasAttribute("tabindex")) main.setAttribute("tabindex", "-1");
          main.focus();
        }}
      >
        Skip to main content
      </a>

      <div className="flex items-center gap-2 px-5 py-4">
        <div className="flex h-8 w-8 items-center justify-center rounded-md bg-primary text-primary-foreground font-bold">
          A
        </div>
        <div className="leading-tight">
          <div className="text-sm font-semibold text-foreground">{APP_NAME}</div>
          <div className="text-[11px] text-muted-foreground">{APP_TAGLINE}</div>
        </div>
      </div>

      <nav aria-label="Main" className="flex-1 overflow-y-auto px-2 py-2">
        {NAV_SECTIONS.map((section) => {
          const items = visible.filter((i) => i.section === section);
          if (items.length === 0) return null;
          return (
            <div key={section} className="mb-4">
              <div
                id={sectionId(section)}
                className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground"
              >
                {section}
              </div>
              {/* Named from its own heading, so the 26 links arrive in labelled
                  groups rather than as one undifferentiated list. */}
              <ul aria-labelledby={sectionId(section)} className="space-y-0.5">
                {items.map((item) => {
                  const active =
                    item.to === "/" ? pathname === "/" : pathname.startsWith(item.to);
                  const Icon = item.icon;
                  const className = cn(
                    "flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-colors",
                    active
                      ? "bg-accent text-accent-foreground font-medium"
                      : "text-muted-foreground hover:bg-accent/50 hover:text-foreground",
                  );

                  if (item.to === ROUTES.askAi) {
                    return (
                      <li key={item.to}>
                        <button type="button" className={className} onClick={openAskAi}>
                          <Icon className="h-4 w-4 shrink-0" />
                          <span>{item.label}</span>
                        </button>
                      </li>
                    );
                  }

                  return (
                    <li key={item.to}>
                      <Link
                        to={item.to}
                        aria-current={active ? "page" : undefined}
                        className={className}
                      >
                        <Icon className="h-4 w-4 shrink-0" />
                        <span>{item.label}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </nav>
    </aside>
  );
}
